// Справочные данные «умного СТО»: виды работ (как writer/builder.WORK_TYPES), состав работы по табл. 1 СТО
// (как lib/sto_check.STRUCT_REQ), названия разделов СТО для группировки находок, мелкие текстовые помощники.
import type { WorkType } from '../../core/types';
import { NBSP } from '../../core/schema/constants';

export interface WorkTypeInfo {
  id: WorkType; name: string; label: string; sub?: string; concl: 'conclusions' | 'conclusion'; intro: boolean;
  grade: string; hint: string;
}

export const WORK_TYPES: WorkTypeInfo[] = [
  { id: 'lab', name: 'Лабораторная работа', label: 'ЛАБОРАТОРНАЯ РАБОТА', concl: 'conclusions', intro: false, grade: 'Отметка о зачете',
    hint: 'Цель, ход работы по разделам, выводы' },
  { id: 'pr', name: 'Практическая работа', label: 'ПРАКТИЧЕСКАЯ РАБОТА', concl: 'conclusions', intro: false, grade: 'Отметка о зачете',
    hint: 'Задание, решение по разделам, выводы' },
  { id: 'kr', name: 'Контрольная работа', label: 'КОНТРОЛЬНАЯ РАБОТА', concl: 'conclusions', intro: true, grade: 'Отметка о зачете',
    hint: 'Введение, основная часть, выводы' },
  { id: 'rgr', name: 'Расчётно-графическая работа', label: 'РАСЧЕТНО-ГРАФИЧЕСКАЯ РАБОТА', concl: 'conclusion', intro: false,
    grade: 'Отметка о зачете', hint: 'Расчёты и графика по заданию, заключение' },
  { id: 'referat', name: 'Реферат', label: 'РЕФЕРАТ', concl: 'conclusion', intro: true, grade: 'Отметка о зачете',
    hint: 'Введение, обзор по разделам, заключение, список источников' },
  { id: 'essay', name: 'Эссе', label: 'ЭССЕ', concl: 'conclusion', intro: true, grade: 'Отметка о зачете',
    hint: 'Введение, рассуждение, заключение' },
  { id: 'kursovaya', name: 'Курсовая работа', label: 'КУРСОВАЯ РАБОТА', concl: 'conclusion', intro: true,
    grade: 'Признать, что работа выполнена и защищена с отметкой', hint: 'Задание, реферат, оглавление, введение, главы, заключение, источники' },
  { id: 'kp', name: 'Курсовой проект', label: 'КУРСОВОЙ ПРОЕКТ', concl: 'conclusion', intro: true,
    grade: 'Признать, что работа выполнена и защищена с отметкой', hint: 'Как курсовая работа + графическая часть' },
  { id: 'practice', name: 'Отчёт о практике', label: 'ОТЧЕТ', sub: 'о прохождении практики', concl: 'conclusion', intro: true,
    grade: 'Признать, что отчет выполнен и защищен с отметкой', hint: 'Задание, реферат, введение, разделы, заключение, источники' },
  // чистый лист: только страницы текста по СТО - без титула, оглавления, служебных листов и требований к структуре
  // работы (вставил текст - получил страницы Word по СТО; writer type: sheet, sto_check work_type sheet)
  { id: 'sheet', name: 'Чистый лист', label: '', concl: 'conclusions', intro: false, grade: '',
    hint: 'Только страницы текста по СТО: без титула, оглавления и служебных листов' },
];
export const WT: Record<string, WorkTypeInfo> = Object.fromEntries(WORK_TYPES.map((w) => [w.id, w]));
export const wt = (t: string | undefined) => WT[t || 'lab'] || WT.lab;

// ------------------------------------------------------------------------------------------------ табл. 1 СТО
/** Код вида работы для табл. 1 (как sto_check.norm_work_type): kp -> kursovaya, rgr/pr -> kr/lab, essay -> referat. */
export function t1Type(t: string): string {
  return ({ kp: 'kursovaya', rgr: 'kr', pr: 'lab', essay: 'referat' } as Record<string, string>)[t] || t;
}

/** Название листа задания по виду работы, когда задано просто «Задание» (как writer/builder.ZADANIE_TITLES:
 *  «Задание на лабораторную работу» и т. п. - так требуют на кафедрах). */
export const ZADANIE_TITLE: Record<string, string> = {
  lab: 'Задание на лабораторную работу', pr: 'Задание на практическую работу', kr: 'Задание на контрольную работу',
  rgr: 'Задание на расчетно-графическую работу', kursovaya: 'Задание на курсовую работу', kp: 'Задание на курсовой проект',
  practice: 'Задание на практику' };
/** Заголовок листа задания так, как его выводит экспорт: своё название - как есть, просто «Задание» - по виду работы. */
export function zadanieTitle(m: { type?: string; zadanie?: { title?: string } } | null | undefined): string {
  const t = (m?.zadanie?.title || '').trim();
  if (t && t.toLowerCase() !== 'задание') return t;
  return ZADANIE_TITLE[m?.type || ''] || 'Задание';
}
/** Варианты заголовка листа задания для выбора (текущее название - тоже в списке). */
export function zadanieTitleOptions(m: { type?: string; zadanie?: { title?: string } } | null | undefined): string[] {
  return [...new Set([zadanieTitle(m), 'Задание', ...Object.values(ZADANIE_TITLE)])];
}

/** Виды работ, у которых лист «Задание» в DOCX может быть бланком Прил. Б СТО (writer/taskform.py). */
export const TASK_FORM_TYPES = ['kursovaya', 'kp', 'rgr'];

/** Лист «Задание» в DOCX - бланком Прил. Б (курсовая/КП/РГР, если не выключено «zadanie.form»), иначе - заголовком
 *  и текстом как есть (не все преподаватели принимают бланк). */
export function zadanieForm(m: { type?: string; zadanie?: { form?: boolean } } | null | undefined): boolean {
  return !!m && TASK_FORM_TYPES.includes(m.type || '') && m.zadanie?.form !== false;
}

/** Лист «Оглавление» в отчёте: выключить можно только там, где СТО его не требует (табл. 1: обязателен у практики
 *  и курсовой; у лабораторной и др. - рекомендуется) - meta.toc_page = false (13.10.2026 просили для лабораторной). */
export function tocRequired(m: { type?: string } | null | undefined): boolean {
  const el = ELEMENTS.find((e) => e.id === 'toc');
  return !!m && !!el && el.req.includes(m.type || '');
}
export function tocOn(m: { type?: string; toc_page?: boolean } | null | undefined): boolean {
  if (isSheet(m)) return false;
  return !m || m.toc_page !== false || tocRequired(m);
}

/** Чистый лист: без титула, оглавления, служебных листов, данных работы и требований к её структуре. */
export function isSheet(m: { type?: string } | null | undefined): boolean {
  return m?.type === 'sheet';
}

export type ElementId = 'zadanie' | 'remarks' | 'referat' | 'toc' | 'intro' | 'concl' | 'sources' | 'ai' | 'indep';
export interface ElementInfo { id: ElementId; title: string; req: string[]; rec: string[]; rule: string; note?: string }

/** Элементы работы: обязателен (+) / рекомендуется (р) для видов работ (табл. 1 СТО, профиль sto_check). */
export const ELEMENTS: ElementInfo[] = [
  { id: 'zadanie', title: 'Задание', req: ['kursovaya'], rec: ['referat', 'kr', 'lab', 'practice'], rule: '3.1' },
  { id: 'remarks', title: 'Лист для замечаний', req: [], rec: ['referat', 'kr', 'lab', 'practice', 'kursovaya'], rule: '2.2 (табл. 1)' },
  { id: 'referat', title: 'Реферат / аннотация', req: [], rec: ['kursovaya'], rule: '3.2' },
  { id: 'toc', title: 'Оглавление', req: ['practice', 'kursovaya'], rec: ['referat', 'kr', 'lab'], rule: '3.3',
    note: 'строится автоматически при экспорте' },
  { id: 'intro', title: 'Введение', req: ['referat', 'kursovaya'], rec: ['kr', 'lab', 'practice'], rule: '3.6' },
  { id: 'concl', title: 'Выводы / Заключение', req: ['referat', 'kursovaya', 'kr', 'lab', 'practice'], rec: [], rule: '3.8',
    note: 'профиль требует для всех видов работ' },
  { id: 'sources', title: 'Список использованных источников', req: ['referat', 'kursovaya'], rec: ['kr', 'lab', 'practice'], rule: '3.10, 4.13',
    note: 'заполняется из библиотеки источников' },
  { id: 'ai', title: 'Раздел об использовании ИИ', req: [], rec: [], rule: '3.9', note: 'только если ИИ применялся' },
  // табл. 1: «р» почти для всех видов, «+» для ВКР - здесь без подсказки линтера (лист по желанию автора)
  { id: 'indep', title: 'Сведения о самостоятельности выполнения работы', req: [], rec: [], rule: '2.16, Прил. А',
    note: 'последним листом, после приложений' },
];

/** Образец «Сведений о самостоятельности выполнения работы» (Прил. А СТО). */
export const INDEP_TEMPLATE = (title: string) => [
  `Работа «${title || '_______________'}» выполнена мной самостоятельно.`,
  'Используемые в работе материалы и концепции из публикуемой литературы и других источников имеют ссылки на них.',
  'Электронный вариант работы передан мной на кафедру посредством корпоративной электронной почты.',
  '«___» __________ 20___ г. ____________________ (подпись) ____________________ (инициалы, фамилия)',
];

/** '+' обязателен, 'р' рекомендуется, '' - по желанию. */
export function elementNeed(el: ElementInfo, type: string): '+' | 'р' | '' {
  const t = t1Type(type);
  if (el.req.includes(t)) return '+';
  if (el.rec.includes(t)) return 'р';
  return '';
}

// ------------------------------------------------------------------------------------------------ разделы СТО
export const STO_SECTIONS: Record<string, string> = {
  '1': 'Общие положения', '2': 'Общие положения и структура', '2.2': 'Структурные элементы (табл. 1)',
  '3': 'Содержание работы', '3.1': 'Титульный лист и задание', '3.2': 'Реферат / аннотация', '3.3': 'Оглавление',
  '3.4': 'Нормативные ссылки', '3.5': 'Определения и сокращения', '3.6': 'Введение', '3.7': 'Основная часть',
  '3.8': 'Заключение / выводы', '3.9': 'Использование ИИ', '3.10': 'Список источников', '3.11': 'Приложения',
  '3.12': 'Электронная версия (PDF)', '4': 'Оформление текста', '4.1': 'Общие требования, типографика',
  '4.2': 'Построение текста, заголовки', '4.3': 'Нумерация страниц', '4.4': 'Нумерация, перечисления',
  '4.5': 'Изображения', '4.6': 'Формулы', '4.7': 'Единицы величин', '4.8': 'Терминология', '4.9': 'Таблицы',
  '4.10': 'Примечания и сноски', '4.11': 'Сокращения', '4.12': 'Блок-схемы', '4.13': 'Список источников',
  '4.14': 'Ссылки', '4.15': 'Примеры', '4.16': 'Приложения', '5': 'Графический материал', '6': 'Программный код',
  '6.1': 'Программный код', '7': 'Документация API', '9': 'Использование ИИ',
};

/** Раздел СТО по строке пункта находки: '4.5.4' -> ['4.5', 'Изображения']. */
export function stoSection(rule: string | undefined): [string, string] {
  const r = String(rule || '');
  if (/Прил\.\s*[МН]/.test(r)) return ['4.13', STO_SECTIONS['4.13']];
  if (/Прил\.\s*Б/.test(r)) return ['3.1', STO_SECTIONS['3.1']];
  const m = r.match(/(\d+)(?:\.(\d+))?/);
  if (!m) return ['~', r.includes('профиль') ? 'Профиль оформления' : 'Прочее'];
  const k2 = m[2] ? `${m[1]}.${m[2]}` : m[1];
  if (STO_SECTIONS[k2]) return [k2, STO_SECTIONS[k2]];
  return [m[1], STO_SECTIONS[m[1]] || 'Раздел ' + m[1]];
}

export function sectionSortKey(k: string): number {
  if (k === '~') return 1e9;
  const [a, b] = k.split('.').map(Number);
  return a * 1000 + (b || 0);
}

// ------------------------------------------------------------------------------------------------ текст
/** «Неразрывный» пробел текста отчёта (инициалы, «№ N», «N с.»): U+00A0 только при USE_NBSP (core/schema/constants,
 *  как lib/sto_lib.USE_NBSP), иначе обычный пробел. Пустым ячейкам интерфейса - NBSP_CHAR, не этот. */
export const NB = NBSP;
export const EN = '–';

/** «Лимонникова Елена Владимировна» -> «Е. В. Лимонникова» (как writer short_name). */
export function shortName(full: string): string {
  const p = (full || '').trim().split(/\s+/);
  if (p.length >= 3) return `${p[1][0]}.${NB}${p[2][0]}.${NB}${p[0]}`;
  if (p.length === 2) return `${p[1][0]}.${NB}${p[0]}`;
  return full || '';
}

/** «Шилов Артём Денисович» -> «Шилов А. Д.» (реферат, 3.2.3). */
export function authorInitials(full: string): string {
  const p = (full || '').trim().split(/\s+/);
  return p.length >= 3 ? `${p[0]} ${p[1][0]}.${NB}${p[2][0]}.` : full;
}

/** Соавторы групповой работы (meta.student.coauthors - ФИО через запятую) без повтора основного автора
 *  (как writer/builder.coauthors: титул, реферат, подписи, свойство «Автор»). */
export function coauthorList(st: { student?: string; coauthors?: string } | null | undefined): string[] {
  const main = (String(st?.student || '').trim().split(/\s+/)[0] || '').replace(/ё/g, 'е');
  const out: string[] = [];
  for (const raw of String(st?.coauthors || '').split(/[,;\n]+/)) {
    const x = raw.replace(/\s+/g, ' ').trim();
    if (x && !(main && x.split(' ')[0].replace(/ё/g, 'е') === main) && !out.includes(x)) out.push(x);
  }
  return out;
}

/** Подпись «И. О. Фамилия» по ФИО полностью или «Фамилия И. О.» (реферат: подпись каждого автора). */
export function signName(full: string): string {
  const s = (full || '').replace(/\s+/g, ' ').trim();
  const m = s.match(/^([А-ЯЁA-Z][^\s,]+),?\s+([А-ЯЁA-Z])\.\s*([А-ЯЁA-Z])\.$/);
  if (m) return `${m[2]}.${NB}${m[3]}.${NB}${m[1]}`;
  return shortName(s);
}

/** Ключевое слово строчными, кроме аббревиатур и имён собственных с латиницей, а также имени собственного не в
 * начале, если первое слово строчное: «критерий Найквиста», но «Передаточная Функция» -> «передаточная функция»
 * (как writer keyword_case и lib/sto_keywords.keyword_case). */
export function keywordCase(k: string): string {
  const words = k.trim().split(/\s+/);
  const firstLow = !/^[А-ЯЁ][а-яё]/.test(words[0] || '');
  return words.map((w, i) => (/[A-ZА-ЯЁ]{2}/.test(w) || /[a-zа-яё][A-ZА-ЯЁ]/.test(w) || /^[A-Z]/.test(w)
    || (i > 0 && firstLow && /^[А-ЯЁ][а-яё]/.test(w)) ? w : w.toLowerCase())).join(' ');
}

export function ruPlural(n: number, forms: [string, string, string]): string {
  const a = Math.abs(n) % 100; const b = a % 10;
  if (b === 1 && a !== 11) return forms[0];
  if (b >= 2 && b <= 4 && !(a >= 12 && a <= 14)) return forms[1];
  return forms[2];
}

export function capFirst(s: string): string {
  const i = s.search(/\p{L}/u);
  return i < 0 ? s : s.slice(0, i) + s[i].toUpperCase() + s.slice(i + 1);
}

export function todayRu(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/** Плоский текст форматированного фрагмента (задание, реферат). */
export function richText(doc: any): string {
  if (!doc) return '';
  if (doc.type === 'text') return doc.text || '';
  return (doc.content || []).map(richText).join(doc.type === 'doc' || doc.type === 'stoList' ? '\n' : '');
}

/** Шаблон раздела об ИИ (п. 3.9): фактологическое описание + рефлексия. */
export const AI_TEMPLATE = {
  facts: [
    'При подготовке работы использовалась система ИИ «…» (версия …, доступ через …).',
    'С её помощью выполнялось: …. Промпты: «…».',
    'Результаты ИИ проверены автором: …; в работу включены после доработки: ….',
  ],
  reflection: [
    'Использование ИИ позволило … . Ограничения и ошибки, которые пришлось исправлять: … .',
    'Выводы, расчёты и итоговый текст работы выполнены и проверены автором самостоятельно.',
  ],
};
